import React from 'react'
import { DateTime } from 'luxon'
import qs from 'qs'
import { CANCELLATION_STATUS, SERVICE_TYPE, STATUS } from '~/app/utils/OrderListUtils'
import { isTypeOf, notEmpty, trainClassifyCodeToName } from '~/app/utils'
import { ConvertedOrder } from '~/app/components/order-converter/getCartOrderConverter'
import { OrderDetailsCancellationCategoryOrders } from '~/app/components/order-list/hooks/useCancelInfo'
import { CartTrainOrderTicketStatus, CategoryType } from '~/__generated__/__types__'
import { SharedOrderQuery } from '~/__generated__/app/query/order-shared/order-shared-list.graphql'
import { OrderCompleteV3Query } from '~/__generated__/app/query/order-complete/order-complete.graphql'
import { OrderDetailsV3Query } from '~/__generated__/app/query/order-details/order-details.graphql'
import { OrderListV3Query } from '~/__generated__/app/query/order-list/order-list.graphql'
import conf from '~/app/conf'
import {
  Train02Icon,
  Train07Icon,
  Train08Icon,
  Train09Icon,
  Train16Icon,
  TrainIcon,
  KorailIcon,
  Train02IconH20,
  Train07IconH20,
  Train08IconH20,
  Train09IconH20,
  Train16IconH20,
  TrainIconH20,
  KorailIconH20,
  KorailIconWithId,
} from '~/app/icons'
import { isoToFormat } from '~/app/utils/useIsoToFormat'
import { IBrazePurchaseEventLog, Category, Currency } from '~/app/braze/brazeCustomLog'
import {
  getOrderCompleteBrazeCouponAndPointInfo,
  getBrazeOrderCategory,
  IConverterBrazeCompositeOrderComplete,
} from '~/app/components/order-converter/useOrderCompleteBrazeCustomConverter'
import krNumberToString from '~/app/utils/krNumberToString'
import { IFbqPurchaseData } from '~/app/pixel'
import { removeFalsyAndJoin } from '~/app/utils/filters'

import { periodWithDayOrWeekFormat } from '~/api/utils/datetime'
import { transformTrainNo } from '~/api/utils/train'
import { KinesisLogMetaId, OrderButton, OrderButtonIconType, OrderButtonType } from '../types'

export const getTrainImage = (type: string) => {
  const iconHeight = 14
  const iconWidth = 35

  switch (type) {
    case '00':
      return {
        h16: <TrainIcon />,
        h20: <TrainIconH20 />,
        thumbnail: <TrainIconH20 height={iconHeight} width={iconWidth} />,
      }
    case '02':
      return {
        h16: <Train02Icon />,
        h20: <Train02IconH20 />,
        thumbnail: <Train02IconH20 height={iconHeight} width={iconWidth} />,
      }
    case '07':
      return {
        h16: <Train07Icon />,
        h20: <Train07IconH20 />,
        thumbnail: <Train07IconH20 height={iconHeight} width={iconWidth} />,
      }
    case '08':
      return {
        h16: <Train08Icon />,
        h20: <Train08IconH20 />,
        thumbnail: <Train08IconH20 height={iconHeight} width={iconWidth} />,
      }
    case '09':
      return {
        h16: <Train09Icon />,
        h20: <Train09IconH20 />,
        thumbnail: <Train09IconH20 height={iconHeight} width={iconWidth} />,
      }
    case '10':
      return {
        h16: <TrainIcon />,
        h20: <TrainIconH20 />,
        thumbnail: <TrainIconH20 height={iconHeight} width={iconWidth} />,
      }
    case '16':
      return {
        h16: <Train16Icon />,
        h20: <Train16IconH20 />,
        thumbnail: <Train16IconH20 height={iconHeight} width={iconWidth} />,
      }
    default:
      return {
        h16: <KorailIcon />,
        h20: <KorailIconH20 />,
        thumbnail: <KorailIconWithId height={iconHeight} width={iconWidth} />,
      }
  }
}

export const getTrainStatus = (
  status: CartTrainOrderTicketStatus,
  arrivalAt: string,
  currentAt?: string
) => {
  const now = currentAt ? DateTime.fromISO(currentAt) : DateTime.now()
  return status === CartTrainOrderTicketStatus.Canceled
    ? STATUS.CANCEL_COMPLETED
    : status === CartTrainOrderTicketStatus.Reserved
      ? DateTime.fromISO(arrivalAt).diff(now, ['second']).seconds < 0
        ? STATUS.COMPLETED
        : STATUS.READY
      : STATUS.READY
}

export const getTrainSubTitle = ({ departureAt, arrivalAt }) => {
  // 기차, 버스 통일된 시간 포맷을 위해 공용 함수 사용
  return periodWithDayOrWeekFormat({
    from: separateIsoTime(departureAt),
    to: arrivalAt,
  })
}

export const getDetailOrderTrainInfo = param => {
  const { seats, adults, child, trainNo, stationTrainClassifyCode, adultFare, childFare } = param
  const sortedSeats = seats.reduce(
    (prev = {}, seat) => ({
      ...prev,
      [seat.carriageNo]: [...(prev[seat.carriageNo] || []), seat.seatDescription],
    }),
    {}
  )
  const seatDescription = Object.keys(sortedSeats).map(
    key => `${Number(key)}호차 ${sortedSeats[key].join(', ')}`
  ) // ['1호차 11A', '2호차 22C, 33D']

  const trainType = `${trainClassifyCodeToName(stationTrainClassifyCode)}-${transformTrainNo(
    trainNo
  )}`
  return [
    [trainType, ...seatDescription],
    [`성인 ${krNumberToString(adultFare)} x ${adults}명`],
    child > 0 ? [`아동 ${krNumberToString(childFare)} x ${child}명`] : null,
  ].filter(notEmpty)
}

export const getTrainInfo = ({ seats, adults, child, trainNo, stationTrainClassifyCode }) => {
  const sortedSeats = seats.reduce(
    (prev = {}, seat) => ({
      ...prev,
      [seat.carriageNo]: [...(prev[seat.carriageNo] || []), seat.seatDescription],
    }),
    {}
  )
  const seatDescription = Object.keys(sortedSeats).map(
    key => `${Number(key)}호차 ${sortedSeats[key].join(', ')}`
  ) // ['1호차 11A', '2호차 22C, 33D']
  const passengers = [adults ? `성인 ${adults}` : null, child ? `아동 ${child}` : null]
    .filter(notEmpty)
    .join(' · ') // 성인 1 · 아동 2
  const trainType = `${trainClassifyCodeToName(stationTrainClassifyCode)}-${transformTrainNo(
    trainNo
  )}`
  return [[trainType, ...seatDescription, passengers]]
}

export const getTrainButtons = (
  props: {
    status: CartTrainOrderTicketStatus
    arrivalAt: string
    id?: string | number
  },
  categoryOrderId: string,
  currentAt: string,
  kinesisLogMetaId: KinesisLogMetaId
) => {
  const { status, arrivalAt, id } = props
  const buttons: Array<OrderButton> = []
  const orderStatus = getTrainStatus(status, arrivalAt, currentAt)

  if (orderStatus === STATUS.READY) {
    buttons.push(
      {
        type: OrderButtonType.BASIC,
        title: '예약 취소',
        id: 'cancel',
        isDisabled: false,
        data: {
          id,
          categoryOrderId,
        },
      },
      {
        type: OrderButtonType.BASIC,
        title: '이용 정보',
        id: 'information',
        data: { type: 'train', categoryOrderId },
      }
    )
    buttons.push({
      type: OrderButtonType.PRIMARY,
      title: '승차권 확인',
      id: 'check-ticket',
      icon: OrderButtonIconType.TICKET,
      data: { ...props, kinesisLogMeta: { ...kinesisLogMetaId, productType: 'train' } },
    })
  }

  return buttons
}

export type CartTrainOrder = Extract<
  OrderListV3Query['orderListV3']['orders'][number]['categoryOrders'][number],
  { __typename?: 'CartTrainOrder' }
>
type CartTrainOrderTicket = CartTrainOrder['tickets'][number]
type OrderDetailsTrain = Extract<
  OrderDetailsV3Query['orderDetailsV3']['categoryOrders'][number],
  { __typename?: 'OrderDetailsTrain' }
>
type OrderDetailsTrainTicket = OrderDetailsTrain['tickets'][number]

const isOrderDetailsTrainOrderTicket = isTypeOf<OrderDetailsTrainTicket>('OrderDetailsTrainTicket')
const separateIsoTime = (isoTime: string) => {
  const [dateTime] = isoTime.split('+')
  const [date, time] = dateTime.split('T')
  return { date, time }
}
type Ticket = OrderDetailsTrainTicket | CartTrainOrderTicket
export const toTrainOrder = (
  props: Ticket,
  categoryOrderId: string,
  currentAt: string,
  kinesisLogMetaId: KinesisLogMetaId
): ConvertedOrder => {
  const {
    id,
    status,
    departureStationName,
    departureStationCode,
    departureAt,
    arrivalStationName,
    arrivalStationCode,
    arrivalAt,
    adults,
    child,
    trainNo,
    seats,
    stationTrainClassifyCode,
    cancelPolicySummary,
  } = props
  const { price = undefined } = isOrderDetailsTrainOrderTicket(props) ? props : {}
  const departureDate = separateIsoTime(departureAt).date
  const params = {
    departureStationCode,
    arrivalStationCode,
    departureDate,
    tripType: 'oneway',
  }
  const subHomeQuery = qs.stringify(params, { encodeValuesOnly: true })
  // Android 에서는 & 까지 encoding 해야함.
  const subHomeDeepLinkQuery = encodeURIComponent(subHomeQuery)

  const pdpUrl = `${conf.web.url}/train`
  const deeplink = `yanoljamotel://wv?url=${pdpUrl}?${subHomeDeepLinkQuery}`

  return {
    id: String(id),
    categoryOrderId,
    status: getTrainStatus(status, arrivalAt, currentAt),
    serviceType: SERVICE_TYPE.TRAIN,
    categoryType: CategoryType.DomesticTrain,
    imageSrc: getTrainImage(stationTrainClassifyCode).thumbnail,
    title: `${departureStationName} - ${arrivalStationName}`,
    subTitle: getTrainSubTitle({ departureAt, arrivalAt }),
    info: isOrderDetailsTrainOrderTicket(props)
      ? getDetailOrderTrainInfo({
          seats,
          adults,
          child,
          trainNo,
          stationTrainClassifyCode,
          adultFare: props.adultFare,
          childFare: props.childFare,
        })
      : getTrainInfo({ seats, adults, child, trainNo, stationTrainClassifyCode }),
    buttons: getTrainButtons(props, categoryOrderId, currentAt, kinesisLogMetaId),
    price,
    pdpUrl: `${pdpUrl}?${subHomeQuery}`,
    deeplink,
    stationTrainClassifyCode,
    useEndDate: isoToFormat(arrivalAt, 'yyyy-MM-dd'),
    cancelPolicySummary,
  }
}

export const toCartTrainOrder = (
  order: CartTrainOrder,
  currentAt: string,
  kinesisLogMetaId: KinesisLogMetaId
) => order.tickets.map(ticket => toTrainOrder(ticket, order.id, currentAt, kinesisLogMetaId))

export type SharedOrderTrain = Extract<
  SharedOrderQuery['sharedOrder']['categoryOrders'][number],
  { __typename?: 'SharedOrderTrain' }
>
export const toSharedTrainOrder = (order: SharedOrderTrain) =>
  order.tickets.map(ticket => {
    const {
      departureStationName,
      arrivalStationName,
      departureAt,
      arrivalAt,
      seats,
      adults,
      child,
      trainNo,
      id,
      stationTrainClassifyCode,
    } = ticket
    return {
      categoryOrderId: order.id,
      serviceType: SERVICE_TYPE.TRAIN,
      categoryType: CategoryType.DomesticTrain,
      imageSrc: getTrainImage(stationTrainClassifyCode).h16,
      title: `${departureStationName} - ${arrivalStationName}`,
      subTitle: getTrainSubTitle({ departureAt, arrivalAt }),
      info: getTrainInfo({ seats, adults, child, trainNo, stationTrainClassifyCode }),
      id,
    }
  })

export type OrderDetailsCancellationTrainOrder = Extract<
  OrderDetailsCancellationCategoryOrders[number],
  { __typename?: 'OrderDetailsCancellationTrain' }
>
export type CancellationTicket = OrderDetailsCancellationTrainOrder['tickets'][number]

// 예약 내역 > 취소된 상품 상세보 > 취소 및 환불 정보
export const toCancellationTrainOrder = (ticket: CancellationTicket, categoryOrderId: string) => {
  const {
    id,
    departureStationName,
    arrivalStationName,
    departureAt,
    arrivalAt,
    seats,
    adults,
    child,
    trainNo,
    price,
    commissionAmount,
    stationTrainClassifyCode,
  } = ticket
  const title = `${departureStationName} - ${arrivalStationName}`
  const subTitle = getTrainSubTitle({ departureAt, arrivalAt })
  const info = getTrainInfo({ seats, adults, child, trainNo, stationTrainClassifyCode })
  return {
    id: String(id),
    categoryOrderId,
    serviceType: SERVICE_TYPE.TRANSPORTATION,
    status: CANCELLATION_STATUS.COMPLETED,
    imageSrc: getTrainImage(stationTrainClassifyCode).thumbnail,
    refundPrice: price,
    commissionAmount,
    title,
    subTitle,
    info,
  }
}

type OrderCompleteCategoryOrder = OrderCompleteV3Query['orderDetailsV3']['categoryOrders'][number]
export type OrderCompleteTrain = Extract<
  OrderCompleteCategoryOrder,
  { __typename?: 'OrderDetailsTrain' }
>

export const toOrderCompleteProductTrain = (trainOrder: OrderCompleteTrain) => {
  const tickets = trainOrder.tickets
  const [departure, arrival] = tickets
  const { departureStationName, arrivalStationName } = departure
  const title = `${
    tickets.length > 1 ? '[왕복] ' : '[편도] '
  }${departureStationName} - ${arrivalStationName}`
  const status = STATUS.READY // 주문완료 화면에서는 상태를 표현하지 않는다
  const subTitle = getTrainSubTitle(departure)
  const info = getTrainInfo(departure)
  const secondarySubTitle = arrival ? getTrainSubTitle(arrival) : undefined
  const secondaryInfo = arrival ? getTrainInfo(arrival) : undefined
  const imageSrc = getTrainImage(tickets[0].stationTrainClassifyCode).thumbnail
  const cancelPolicySummary = tickets[0].cancelPolicySummary

  return {
    id: trainOrder.id,
    categoryOrderId: trainOrder.id,
    serviceType: SERVICE_TYPE.TRAIN,
    imageSrc,
    title,
    status,
    subTitle,
    info,
    secondarySubTitle,
    secondaryInfo,
    categoryType: CategoryType.DomesticTrain,
    useEndDate: isoToFormat(arrival ? arrival.arrivalAt : departure.arrivalAt, 'yyyy-MM-dd'),
    cancelPolicySummary,
  }
}

export const toOrderCompleteBrazeCustomTrain = ({
  order,
  orderDetails,
  isNolPayment,
}: {
  order: OrderCompleteTrain
  orderDetails: OrderCompleteV3Query['orderDetailsV3']
  isNolPayment: boolean
}): IConverterBrazeCompositeOrderComplete => {
  const { category, tickets } = order
  const [departure, arrival] = tickets
  const {
    departureStationCode,
    arrivalStationCode,
    departureStationName,
    arrivalStationName,
    departureAt,
    stationTrainClassifyCode,
  } = departure
  const arrivalAt = arrival?.departureAt
  const { createdAt, id } = orderDetails
  const formatDate = date => DateTime.fromISO(date).toFormat('yyyy-MM-dd')
  const isSameDate = formatDate(createdAt) === formatDate(departureAt)
  const isAfterDate =
    (DateTime.fromISO(departureAt).diff(DateTime.fromISO(createdAt), 'days').toObject().days || 0) >
    0

  const has_children = tickets.some(ticket => ticket.child > 0)
  const price = tickets.reduce((total, ticket) => total + ticket.price, 0)
  const subcategory = [
    trainClassifyCodeToName(stationTrainClassifyCode),
    arrival && trainClassifyCodeToName(arrival?.stationTrainClassifyCode),
  ]
    .filter(notEmpty)
    .join(', ')

  const data: IConverterBrazeCompositeOrderComplete = {
    category: getBrazeOrderCategory(order),
    subcategory,
    composite_order_id: id,
    property_category: category,
    item_id: `${departureStationCode}, ${arrivalStationCode}`,
    item_name: `${departureStationName}, ${arrivalStationName}`,
    check_in_date: DateTime.fromISO(departureAt).toISO(),
    is_earlybird: !isSameDate && isAfterDate,
    cust_count: order.tickets.length,
    has_children,
    ...getOrderCompleteBrazeCouponAndPointInfo({
      orderDetails,
      coupon: order.trainDiscount?.coupons[0],
      price,
    }),
    nolcard_approval: isNolPayment,
    city_name: removeFalsyAndJoin([departureStationName, arrival?.departureStationName]),
  }

  if (arrivalAt) {
    data.check_out_date = DateTime.fromISO(arrivalAt).toISO()
  }
  return data
}

export const getOrderCompleteFacebookPixelTrainData = ({
  order,
}: {
  order: OrderCompleteTrain
}): IFbqPurchaseData => {
  const { tickets } = order
  const [departure, arrival] = tickets
  const price = tickets.reduce((total, ticket) => total + ticket.price, 0)
  const num_adults = tickets[0].adults
  const num_children = tickets[0].child

  const data: IFbqPurchaseData = {
    content_type: 'd_tr',
    content_ids: removeFalsyAndJoin([
      departure.departureStationCode,
      arrival?.departureStationCode,
    ]),
    city: removeFalsyAndJoin([departure.departureStationName, arrival?.departureStationName]),
    region: removeFalsyAndJoin([departure.departureStationName, arrival?.departureStationName]),
    num_adults,
    num_children,
    value: price,
    currency: 'KRW',
    checkin_date: DateTime.fromISO(departure.departureAt).toISO(),
  }
  if (arrival?.departureAt) {
    data.checkout_date = DateTime.fromISO(arrival.departureAt).toISO()
  }
  return data
}

export const toReceiptTrain = (
  props: OrderDetailsTrainTicket,
  categoryOrderId: string
): ConvertedOrder => {
  const {
    id,
    status,
    departureStationName,
    departureAt,
    arrivalStationName,
    arrivalAt,
    adults,
    child,
    trainNo,
    seats,
    stationTrainClassifyCode,
  } = props
  const { price = undefined } = isOrderDetailsTrainOrderTicket(props) ? props : {}

  return {
    id,
    status: getTrainStatus(status, arrivalAt),
    title: `${departureStationName} - ${arrivalStationName}`,
    subTitle: getTrainSubTitle({ departureAt, arrivalAt }),
    serviceType: SERVICE_TYPE.TRAIN,
    categoryOrderId,
    stationTrainClassifyCode,
    info: isOrderDetailsTrainOrderTicket(props)
      ? getDetailOrderTrainInfo({
          seats,
          adults,
          child,
          trainNo,
          stationTrainClassifyCode,
          adultFare: props.adultFare,
          childFare: props.childFare,
        })
      : getTrainInfo({ seats, adults, child, trainNo, stationTrainClassifyCode }),
    categoryType: CategoryType.DomesticTrain,
    price,
  }
}

export const toOrderCompleteBrazePurchaseTrain = (
  trainOrder: OrderCompleteTrain,
  payments: OrderCompleteV3Query['orderDetailsV3']['payments'],
  paid_amount: number
): IBrazePurchaseEventLog => {
  const [departure, arrival] = trainOrder.tickets
  const {
    departureStationCode,
    arrivalStationCode,
    price,
    stationTrainClassifyCode: departureTrainCode,
  } = departure
  const { price: arrivalPrice = 0, stationTrainClassifyCode: arrivalTrainCode } = arrival || {}
  const payment_method = payments.map(payment => payment?.paymentMethod?.code || '')
  const subcategory = [departureTrainCode, arrivalTrainCode].filter(notEmpty).join(', ')
  return {
    productId: `${departureStationCode}, ${arrivalStationCode}`,
    price: price + arrivalPrice,
    currencyCode: Currency.KRW,
    purchaseProperties: {
      category: Category.TRAIN,
      subcategory,
      payment_method,
      paid_amount,
    },
  }
}
